import { http } from "@/composable/httpService";
import { useHandleErrorStatus } from "@/composable/useHandleErrorStatus";
import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import { useLmsSettingsStore } from "../useLmsSettingsStore";
import { computed } from "vue";
import { useEnrolledCourseStore } from "../enrolledCourseStore";

export const useHomeCoursesStore = defineStore("homeCoursesStore", () => {
  const lmsSettingStore = useLmsSettingsStore();
  const enrolledCourseStore = useEnrolledCourseStore();
  const { selectedFilter } = storeToRefs(enrolledCourseStore);

  const courses = ref([]);
  const baseCourses = ref([]);
  const loadingCourses = ref(false);
  const search = ref("");
  const originalCourses = ref([]);
  const offset = ref(0);
  const limit = ref(6);
  const hasMore = ref(true);
  const totalCourses = ref(0);
  const loadingCoursesProgress = ref(false);
  const courseProgress = ref([]);
  const hasEnrollment = computed(() => lmsSettingStore.hasEnrollment);

  const homeTab = computed(() => {
    if (hasEnrollment.value) {
      return "Enrolled Courses";
    }
    if (
      hasEnrollment.value &&
      selectedFilter.value &&
      selectedFilter.value.available_course &&
      !selectedFilter.value.enrolled_course
    ) {
      return "Available Courses";
    }

    if (
      hasEnrollment.value &&
      selectedFilter.value &&
      selectedFilter.value.enrolled_course &&
      !selectedFilter.value.available_course
    ) {
      return "Enrolled Courses";
    }

    return "All Courses";
  });

  // Computed property that merges courses with progress data
  const coursesWithProgress = computed(() => {

    if (!baseCourses.value || baseCourses.value.length === 0) return [];

    // If no progress data yet, return base courses
    if (!courseProgress.value || courseProgress.value.length === 0) {
      return baseCourses.value;
    }

    // Create a map of progress data for quick lookup
    const progressMap = new Map(
      courseProgress.value.map(item => [item.course_id, item.progress])
    );

    // Merge progress data with courses
    return baseCourses.value.map(course => ({
      ...course,
      completed_percentage: progressMap.get(course.id) ?? course.completed_percentage ?? 0
    }));
  });

  // Update courses ref when coursesWithProgress changes
  computed(() => {
    return courses.value = coursesWithProgress.value;
  });

  const filteredCourses = computed(() => {
    let newCourses = courses.value;
    // filtering
    if (selectedFilter.value && selectedFilter.value.filter) {
      const filter = selectedFilter.value.filter;
      switch (filter) {
        case "not started":
          newCourses = courses.value.filter((course) => {
            return course.completed_percentage == 0;
          });
          break;
        case "in progress":
          newCourses = courses.value.filter((course) => {
            return (
              course.completed_percentage > 0 &&
              course.completed_percentage < 100
            );
          });
          break;
        case "completed":
          newCourses = courses.value.filter((course) => {
            return course.completed_percentage == 100;
          });
          break;
        case "all":
          newCourses = courses.value;
          break;
      }
    }

    // sorting
    if (selectedFilter.value && selectedFilter.value.sort) {
      const sort = selectedFilter.value.sort;
      switch (sort) {
        case "a-z":
          newCourses = newCourses.sort((a, b) =>
            a.name?.toLowerCase() > b.name?.toLowerCase()
              ? 1
              : b.name?.toLowerCase() > a.name?.toLowerCase()
              ? -1
              : 0
          );
          break;
        case "z-a":
          newCourses = newCourses.sort((a, b) =>
            a.name?.toLowerCase() < b.name?.toLowerCase()
              ? 1
              : b.name?.toLowerCase() < a.name?.toLowerCase()
              ? -1
              : 0
          );
          break;
        case "lth":
          newCourses = newCourses.sort((a, b) =>
            a.completed_percentage > b.completed_percentage
              ? 1
              : b.completed_percentage > a.completed_percentage
              ? -1
              : 0
          );
          break;
        case "htl":
          newCourses = newCourses.sort((a, b) =>
            a.completed_percentage < b.completed_percentage
              ? 1
              : b.completed_percentage < a.completed_percentage
              ? -1
              : 0
          );
          break;
        case "default":
          newCourses = newCourses.sort((a, b) =>
            a.id > b.id ? 1 : b.id > a.id ? -1 : 0
          );
          break;
      }
    }

    return newCourses;
  });

  const resetPagination = () => {
    offset.value = 0;
    courses.value = [];
    hasMore.value = true;
    totalCourses.value = 0;
    courseProgress.value = [];
  };

  const handleFetchCourses = async () => {
    loadingCourses.value = true;
    http()
      .post("/v3/courses/list", {
        keyword: search.value,
        ...selectedFilter.value,
      })
      .then(({ data }) => {
        baseCourses.value = data;
        courses.value = data;
        hasMore.value = false;
        if (
          hasEnrollment.value &&
          courses.value.length === 0 &&
          selectedFilter.value.enrolled_course &&
          !selectedFilter.value.available_course
        ) {
          //
          selectedFilter.value = {
            available_course: true,
            enrolled_course: false,
            course_category: selectedFilter.value.course_category,
            filter: selectedFilter.value.filter,
            sort: selectedFilter.value.sort,
          };
          handleFetchCourses();
          return;
        }
        originalCourses.value = structuredClone(courses.value);
        const courseIds = baseCourses.value.map(course => course.id);
        handleFetchCourseProgress(courseIds)
      }).catch((error) => useHandleErrorStatus(error))
      .finally(() => (loadingCourses.value = false));
    // try {
    //   loadingCourses.value = true;
    //   const { data } = await http().post("/v3/courses/list", {
    //     keyword: search.value,
    //     ...selectedFilter.value,
    //   });
    //   baseCourses.value = data;
    //   courses.value = data;
    //   if (
    //     hasEnrollment.value &&
    //     courses.value.length === 0 &&
    //     selectedFilter.value.enrolled_course &&
    //     !selectedFilter.value.available_course
    //   ) {
    //     //
    //     selectedFilter.value = {
    //       available_course: true,
    //       enrolled_course: false,
    //       course_category: selectedFilter.value.course_category,
    //       filter: selectedFilter.value.filter,
    //       sort: selectedFilter.value.sort,
    //     };
    //     handleFetchCourses();
    //     return;
    //   }
    //   originalCourses.value = structuredClone(courses.value);
    // } catch (error) {
    //   useHandleErrorStatus(error);
    // } finally {
    //   loadingCourses.value = false;
    // }
  };

  // Updated paginated fetch function
  const handleFetchCoursePaginated = async (fetchCourseProgress = false) => {
    resetPagination();
    loadingCourses.value = true;
    http()
      .get("/v3/courses", {
        params: {
          keyword: search.value,
          offset: offset.value,
          limit: limit.value,
          ...selectedFilter.value,
        },
      })
      .then(({ data }) => {
        if (data && data.data) {
          baseCourses.value = data.data;
          courses.value = data.data;
          hasMore.value = data.meta.has_more;
          totalCourses.value = data.meta.total;
          offset.value = Number(data.meta.offset) + Number(data.meta.limit);
        }

        if (
          hasEnrollment.value &&
          courses.value.length === 0 &&
          selectedFilter.value?.enrolled_course &&
          !selectedFilter.value?.available_course
        ) {
          selectedFilter.value = {
            available_course: true,
            enrolled_course: false,
            course_category: selectedFilter.value.course_category,
            filter: selectedFilter.value.filter,
            sort: selectedFilter.value.sort,
          };
          handleFetchCourses();
          return;
        }

        if (fetchCourseProgress) {
          handleFetchCourseProgress();
        }
      })
      .catch((error) => useHandleErrorStatus(error))
      .finally(() => (loadingCourses.value = false));
    // try {
    //   resetPagination();
    //   loadingCourses.value = true;

    //   const { data } = await http().get("/v3/courses", {
    //     params: {
    //       keyword: search.value,
    //       offset: offset.value,
    //       limit: limit.value,
    //       ...selectedFilter.value,
    //     }
    //   });

    //   if (data && data.data) {
    //     baseCourses.value = data.data;
    //     courses.value = data.data;
    //     hasMore.value = data.meta.has_more;
    //     totalCourses.value = data.meta.total;
    //     offset.value = Number(data.meta.offset) + Number(data.meta.limit);
    //   }

    //   if (
    //     hasEnrollment.value &&
    //     courses.value.length === 0 &&
    //     selectedFilter.value?.enrolled_course &&
    //     !selectedFilter.value?.available_course
    //   ) {
    //     selectedFilter.value = {
    //       available_course: true,
    //       enrolled_course: false,
    //       course_category: selectedFilter.value.course_category,
    //       filter: selectedFilter.value.filter,
    //       sort: selectedFilter.value.sort,
    //     };
    //     await handleFetchCourses();
    //   }
    // } catch (error) {
    //   useHandleErrorStatus(error);
    // } finally {
    //   loadingCourses.value = false;
    // }
  };

  // Load more courses (pagination)
  const loadMoreCourses = async () => {
    if (!hasMore.value || loadingCourses.value) return;

    try {
      loadingCourses.value = true;

      const { data } = await http().get("/v3/courses", {
        params: {
          keyword: search.value,
          offset: offset.value,
          limit: limit.value,
          ...selectedFilter.value,
        }
      });

      if (data && data.data) {
        baseCourses.value = [...baseCourses.value, ...data.data];
        courses.value = [...courses.value, ...data.data];
        hasMore.value = data.meta.has_more;
        totalCourses.value = data.meta.total;
        offset.value = Number(data.meta.offset) + Number(data.meta.limit);
      }
    } catch (error) {
      useHandleErrorStatus(error);
    } finally {
      loadingCourses.value = false;
    }
  };

  const handleFetchCourseProgress = async (courseIds = [], appendMode = false) => {
    try {
      loadingCoursesProgress.value = true;

      // If no courseIds provided, extract from current courses
      if (courseIds.length === 0) {
        courseIds = baseCourses.value.map(course => course.id);
      }

      // Only fetch if we have course IDs
      if (courseIds.length === 0) {
        console.log('No course IDs to fetch progress for');
        return;
      }

      const { data } = await http().get("/v3/progress-calculation/all-courses", {
        params: {
          course_ids: courseIds
        }
      });

      // Handle the response structure from your backend
      const newProgressData = data.completion_percentages || [];

      if (appendMode) {
        // Append new progress data to existing data
        // Create a Map of existing progress for deduplication
        const existingProgressMap = new Map(
          courseProgress.value.map(item => [item.course_id, item])
        );

        // Add new progress data, overwriting any duplicates
        newProgressData.forEach(item => {
          existingProgressMap.set(item.course_id, item);
        });

        // Convert back to array
        courseProgress.value = Array.from(existingProgressMap.values());
      } else {
        // Replace entire progress array (initial load)
        courseProgress.value = newProgressData;
      }

    } catch (error) {
      useHandleErrorStatus(error);
      console.log('Error fetching course progress');
    } finally {
      loadingCoursesProgress.value = false;
    }
  }

  // Helper method to reset progress data
  const resetProgressData = () => {
    courseProgress.value = [];
  };

  return {
    courses,
    baseCourses,
    handleFetchCourses,
    handleFetchCoursePaginated,
    handleFetchCourseProgress,
    loadMoreCourses,
    homeTab,
    originalCourses,
    filteredCourses,
    loadingCourses,
    offset,
    hasMore,
    totalCourses,
    limit,
    resetProgressData
  };
});
